import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { ArrowLeftIcon } from '@heroicons/react/outline'

import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '../ui/form'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Label } from '../ui/label'
import useFacility from '../../utilities/hooks/useFacility'

function FacilitiesDisclosuresNew() {
  const history = useHistory()
  const { facility } = useFacility({ isSubscriptionRequired: false })
  const [submitError, setSubmitError] = useState(null)

  const form = useForm({
    defaultValues: {
      name: '',
      active: true,
      required: false,
      disclosureType: 'one_liner',
      agreement: ''
    }
  })

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting }
  } = form
  const disclosureType = watch('disclosureType')
  const agreementText = watch('agreement')
  const isActive = watch('active')
  const isRequired = watch('required')

  async function onSubmit(data) {
    setSubmitError(null)
    try {
      await axios.post(`/api/facilities/${facility.id}/disclosures`, {
        disclosure: {
          name: data.name,
          active: data.active,
          required: data.required,
          disclosure_type: data.disclosureType,
          agreement: data.agreement
        }
      })

      history.push({
        pathname: `/facilities/${facility.id}/disclosures`,
        state: {
          notification: {
            message: 'Disclosure created successfully!',
            color: 'green'
          }
        }
      })
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.error) || 'Failed to create disclosure'
      setSubmitError(errorMessage)
    }
  }

  function handleCancel() {
    history.push(`/facilities/${facility.id}/disclosures`)
  }

  return (
    <ApplicationLayout>
      <div className="min-h-screen pb-10 bg-gray-100">
        <div className="px-4 py-6 mx-auto max-w-4xl sm:px-6 lg:px-8">
          <button
            onClick={handleCancel}
            className="inline-flex items-center mb-6 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Disclosures
          </button>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Create New Disclosure</h1>
            <p className="mt-1 text-sm text-gray-600 sm:mt-2">
              Add a disclosure agreement for students to review during registration
            </p>
          </div>

          {submitError && <div className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-md">{submitError}</div>}

          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="p-6 bg-white rounded-lg shadow">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Internal Information</h2>
                <p className="mb-6 text-sm text-gray-600">For your reference. Not shown to registrants.</p>

                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ required: 'Name is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter disclosure name" {...field} autoFocus />
                        </FormControl>
                        <FormDescription>For your reference only</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Active</FormLabel>
                          <FormDescription>
                            {isActive
                              ? 'Registrants will be presented with this disclosure'
                              : 'Registrants will not be presented with this disclosure'}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="required"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Required</FormLabel>
                          <FormDescription>
                            {isRequired
                              ? 'Registrants are required to agree or cannot register'
                              : 'Registrants can decline to agree and still register'}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="p-6 bg-white rounded-lg shadow">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Agreement Details</h2>
                <p className="mb-6 text-sm text-gray-600">This information is shown during registration.</p>

                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="disclosureType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="one_liner">One-liner</SelectItem>
                            <SelectItem value="document">Document</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="agreement"
                    rules={{ required: 'Agreement text is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Agreement</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="I agree to..."
                            rows={disclosureType === 'document' ? 10 : 3}
                            maxLength={disclosureType === 'one_liner' ? 140 : undefined}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>This is what the registrant accepts</FormDescription>
                        {disclosureType === 'one_liner' && (
                          <p className="text-xs text-gray-500">{agreementText?.length || 0}/140 characters</p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="p-4 bg-gray-50 rounded-md">
                    <h4 className="mb-2 text-sm font-medium text-gray-900">Preview</h4>
                    <p className="text-sm text-gray-600">
                      {agreementText || 'Your agreement text will appear here...'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md sm:w-auto hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md sm:w-auto hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="w-5 h-5 mr-2 -ml-1 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Disclosure'
                  )}
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </ApplicationLayout>
  )
}

export default FacilitiesDisclosuresNew
